'use client';

import { useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Loader2 } from 'lucide-react';
import type { Locale } from '@/lib/content';
import { homeHref } from '@/lib/utils';

interface SignupClientProps {
  locale: Locale;
}

const translations = {
  nl: {
    title: 'Registreren',
    description: 'Je wordt doorgestuurd naar het registratieportaal van Winkel Factuur. Maak een gratis account aan en automatiseer je facturatie voor Shopify, WooCommerce, bol.com en Amazon.',
    redirecting: 'Je wordt doorgestuurd naar de registratiepagina...',
  },
  en: {
    title: 'Sign Up',
    description: 'You are being redirected to the Winkel Factuur registration portal. Create a free account and automate your invoicing for Shopify, WooCommerce, bol.com and Amazon.',
    redirecting: 'Redirecting you to the registration page...',
  },
  de: {
    title: 'Registrieren',
    description: 'Sie werden zum Registrierungsportal von Winkel Factuur weitergeleitet. Erstellen Sie ein kostenloses Konto und automatisieren Sie Ihre Rechnungsstellung für Shopify, WooCommerce, bol.com und Amazon.',
    redirecting: 'Sie werden zur Registrierungsseite weitergeleitet...',
  },
  fr: {
    title: 'Inscription',
    description: 'Vous allez être redirigé vers le portail d\'inscription de Winkel Factuur. Créez un compte gratuit et automatisez votre facturation pour Shopify, WooCommerce, bol.com et Amazon.',
    redirecting: 'Redirection vers la page d\'inscription...',
  },
};

export function SignupClient({ locale }: SignupClientProps) {
  const t = translations[locale] || translations.nl;
  const dashboardRegisterUrl = 'https://dash.winkelfactuur.nl/register';

  useEffect(() => {
    // Track in GTM
    if ((window as any).dataLayer) {
      (window as any).dataLayer.push({
        event: 'signup_redirect',
        signup_source: 'factpromo',
        locale: locale,
      });
    }

    // Redirect immediately to dashboard register
    window.location.href = dashboardRegisterUrl;
  }, [locale]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 px-4">
      <div className="w-full max-w-sm text-center">
        {/* Logo */}
        <div className="mb-8 flex justify-center">
          <Link href={homeHref(locale)} className="inline-block">
            <Image
              src="/logo.svg"
              alt="Winkel Factuur"
              width={280}
              height={32}
              priority
              className="h-10 w-auto"
            />
          </Link>
        </div>

        {/* Redirecting Card */}
        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-xl">
          <h1 className="text-xl font-bold text-slate-900 mb-2 text-center">{t.title}</h1>
          <p className="text-sm text-slate-500 mb-4 text-center">{t.description}</p>
          <div className="flex flex-col items-center justify-center py-4">
            <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
            <p className="mt-4 text-sm text-slate-600">{t.redirecting}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
